/**
 * Freehand drawing renderer (registries/element-types/drawing per docs/08 §2:
 * one folder per element type; the canvas layer knows nothing about it).
 *
 * The stroke's vertices (`payload.d`) are ELEMENT-LOCAL board units captured at
 * the element's creation size. Rendering into an SVG whose `viewBox` is that
 * original box with `preserveAspectRatio="none"` makes a later resize scale the
 * whole drawing to the element's current `w`/`h` (the wrapper sizes us, we
 * fill) — exactly like the image renderer. `overflow: visible` + the box's
 * half-thickness padding keep round caps from clipping, and let a cell-anchored
 * drawing cross its cell's lines (F-36: it FOLLOWS the cell but is not confined).
 */
import { isDrawingElementPayload } from "@kola/shared";

import type { ElementRendererProps } from "../image/ImageElement.js";

/** Minimum pointer-target width (board px) so a thin stroke is still grabbable. */
const MIN_HIT_STROKE = 12;

export function DrawingElementView({ element }: ElementRendererProps) {
  const payload = element.payload;
  if (!isDrawingElementPayload(payload)) return null;
  const { d, color, thickness } = payload;
  if (d.length < 2) return null;

  // The viewBox is the ORIGINAL captured box (element w/h at creation ≈ the
  // point extent + padding). Points are already local to it.
  const points: string[] = [];
  for (let i = 0; i + 1 < d.length; i += 2) points.push(`${d[i]},${d[i + 1]}`);

  // Hit-testing is on the INK, not the bounding box: the wrapper for a drawing
  // has pointer-events: none (a stroke's box is mostly transparent and often
  // covers grid cells — it must not swallow their clicks), and the invisible
  // widened "hit" line below re-enables events exactly along the stroke.
  // Events bubble from it to the wrapper, so select/drag work unchanged.
  return (
    <svg
      className="element-drawing"
      width={element.w}
      height={element.h}
      viewBox={`0 0 ${element.w} ${element.h}`}
      preserveAspectRatio="none"
      style={{ overflow: "visible", display: "block", pointerEvents: "none" }}
      aria-hidden="true"
    >
      {points.length === 1 ? (
        // A single-point stroke is a dot — a zero-length line with round caps
        // would not paint, so draw a filled circle of the stroke radius (plus
        // an invisible widened hit circle, same contract as the line case).
        <>
          <circle
            cx={d[0]}
            cy={d[1]}
            r={Math.max(thickness, MIN_HIT_STROKE) / 2}
            fill="transparent"
            style={{ pointerEvents: "auto", cursor: "move" }}
          />
          <circle cx={d[0]} cy={d[1]} r={thickness / 2} fill={color} />
        </>
      ) : (
        <>
          <polyline
            points={points.join(" ")}
            fill="none"
            stroke="transparent"
            strokeWidth={Math.max(thickness, MIN_HIT_STROKE)}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pointerEvents: "stroke", cursor: "move" }}
          />
          <polyline
            points={points.join(" ")}
            fill="none"
            stroke={color}
            strokeWidth={thickness}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )}
    </svg>
  );
}
